import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Map } from 'immutable';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { Account } from '../../../account-data/account.model';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../../../dialog/dialog.service';
import {
  CHARGE_A_CARD_ACTION,
  DOCUMENTS_NEW_ROUTE,
  DocumentTypesEnum,
  MANUAL_PAYMENT_ACTION,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { DocumentPayments } from '../../../document-data/sub-models/document-payments.model';
import { States } from '../../../document-data/sub-models/states.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { NavigationService } from '../../../navigation/navigation.service';
import {
  IAddManualPayment,
  IInvalidEmailConfirmDialogData,
  ISendReceipt,
  ISendReceiptPreview,
} from '../../../payments-data/payments-data.data';
import { PaymentsDataDispatcher } from '../../../payments-data/payments-data.dispatcher';
import {
  DIALOG_TYPE as sendReceiptDialog,
  ISendReceiptDialogData,
} from '../../../payments/components/send-receipt-dialog/send-receipt-dialog.component';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { DateTimeService } from 'i2g-ng-utils';
import { EmailService } from '../../../shared/services/email/email.service';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DIALOG_TYPE as turnOnPaymentsDialog } from '../../../payments/components/turn-on-payments-dialog/turn-on-payments-dialog.component';
import { DocumentPaymentActionsBuilder } from './document-payment-actions.builder';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { filter, finalize, map, take, takeUntil } from 'rxjs/operators';
import { INVOICES_MASTER_ROUTE } from '../../../main/main.data';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import {
  DIALOG_TYPE as manualPaymentDialog,
  IManualPaymentDialogData,
} from '../../../payments/components/manual-payment-dialog/manual-payment-dialog.component';
import { IInvoiceDataDispatcher } from '../../../invoice-data/invoice-data-dispatcher.interface';
import { get, noop } from 'lodash';
import { PaymentTransaction } from '../../../document-data/sub-models/payment-transaction.model';
import { UrlService } from '../../../shared/services/url/url.service';
import { ClientDataDispatcher } from '../../../client-data/client-data.dispatcher';
import { ALL_LIST_FILTER } from '../../../entity-data/entity-data.data';

@Component({
  selector: 'app-document-payment-actions',
  templateUrl: './document-payment-actions.component.html',
  styleUrls: ['./document-payment-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentPaymentActionsBuilder],
})
export class DocumentPaymentActionsComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() documentId: string;
  @Input() documentFeatureName: string;
  @Input() documentCalculationResult: CalculationResult;
  @Input() documentPaymentUrl: string;
  @Input() documentDueDate: string;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() documentBilling: Billing;
  @Input() documentPayments: DocumentPayments;
  @Input() companySettings: CompanySettings;
  @Input() paymentsSettings: PaymentsSettings;
  @Input() documentState: States;
  @Input() documentLocale: string;
  @Input() document: Document;
  @Input() documentHasUnsavedChanges: boolean;
  @Input() documentType: DocumentTypesEnum;
  @Input() featureToggles: Toggles;
  @Input() enableDeposits: boolean;
  @Input() showPaymentActions: boolean;
  @Input() addDepositForFuture: DepositToggleValue;
  @Input() deposit: Deposit;
  @Input() account: Account;
  @Input() depositRequestFeatureName: string;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();
  @Output() onUpdateDocumentContent: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() onUpdateSettingsContent: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _paymentRemindersFeatureName: string = Toggles.KEYS.PAYMENT_REMINDERS;

  invalidEmailDialogId: string = v4();
  featureHighlightIcon$: Observable<IIcon>;
  addPaymentButtonTapped: ITrackingAction;
  paymentHistoryButtonTapped: ITrackingAction;
  sendReceiptButtonTapped: ITrackingAction;
  depositRequestAllAccess: boolean;
  depositRequestReadAccess: boolean;
  isEstimate: boolean;
  transactionHistoryRoute: string[];
  documentAllAccess: boolean;
  DocumentTypesEnum: typeof DocumentTypesEnum;
  isKicEnablingPayment$: BehaviorSubject<boolean>;

  constructor(
    private _route: ActivatedRoute,
    private _documentPaymentActionsBuilder: DocumentPaymentActionsBuilder,
    private _paymentsDataDispatcher: PaymentsDataDispatcher,
    private _clientDataDispatcher: ClientDataDispatcher,
    private _featuresDataStore: FeaturesDataStore,
    private _dialogService: DialogService,
    private _navigationService: NavigationService,
    private _emailService: EmailService,
    private _dateTimeService: DateTimeService,
    private _globalBannerService: GlobalBannerService,
    private _utilsService: UtilsService,
    private _urlService: UrlService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.featureToggles || changes.documentFeatureName) &&
      this.featureToggles &&
      this.documentFeatureName
    ) {
      this.documentAllAccess = this.featureToggles.isAllAccess(
        this.documentFeatureName
      );
    }

    if (
      (changes.featureToggles || changes.depositRequestFeatureName) &&
      this.featureToggles &&
      this.depositRequestFeatureName
    ) {
      this.depositRequestAllAccess = this.featureToggles.isAllAccess(
        this.depositRequestFeatureName
      );
      this.depositRequestReadAccess = this.featureToggles.isReadOnlyAccess(
        this.depositRequestFeatureName
      );
    }

    if (changes.documentType && this.documentType) {
      this.isEstimate = this.documentType === DocumentTypesEnum.estimate;
    }
  }

  ngOnInit() {
    this.addPaymentButtonTapped = this._trackingActionBuilder.buildButtonTapped(
      'add_payment',
      this.trackingScreenName
    );
    this.paymentHistoryButtonTapped = this._trackingActionBuilder.buildButtonTapped(
      'payment_history',
      this.trackingScreenName
    );
    this.sendReceiptButtonTapped = this._trackingActionBuilder.buildButtonTapped(
      'payment_receipts',
      this.trackingScreenName
    );
    this.transactionHistoryRoute = ['../transaction-history'];
    this.DocumentTypesEnum = DocumentTypesEnum;
    this.featureHighlightIcon$ = this._featuresDataStore
      .hasFeatureHighlightCanvas(this._paymentRemindersFeatureName)
      .pipe(
        filter((x) => !!x),
        map(
          (): IIcon => {
            return {
              name: 'premium-feature',
              size: IconSizesEnum.medium,
              customClass: 'premium-icon',
            };
          }
        )
      );
    this.isKicEnablingPayment$ = new BehaviorSubject<boolean>(false);
  }

  ngAfterViewInit() {
    this._route.queryParamMap
      .pipe(
        filter(
          (queryParamMap) =>
            queryParamMap.get('action') === MANUAL_PAYMENT_ACTION
        ),
        take(1),
        takeUntil(this._destroyed)
      )
      .subscribe(() => this.openManualPaymentDialog(true));

    this._route.queryParamMap
      .pipe(
        filter(
          (queryParamMap) =>
            queryParamMap.get('action') === CHARGE_A_CARD_ACTION
        ),
        take(1),
        takeUntil(this._destroyed)
      )
      .subscribe(() =>
        this._dialogService.openDialog({ type: manualPaymentDialog })
      );
  }

  openManualPaymentDialog(asFullyPaid: boolean = false) {
    if (!this.documentAllAccess || !this.document) {
      return;
    }

    this._getLatestSavedDocument()
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe(
        (document: Document) => {
          const dialogData: IManualPaymentDialogData = {
            asFullyPaid,
            document,
          };

          this._dialogService.openDialog({
            type: manualPaymentDialog,
            data: dialogData,
          });
        },
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  openSendReceiptDialog() {
    if (!this.documentAllAccess) {
      return;
    }

    const email = get(this.documentBilling, Billing.KEYS.EMAIL, '');

    if (!email || !this._emailService.validateEmails(email)) {
      this._dialogService.openConfirmDialog(this.invalidEmailDialogId, {
        documentType: this.documentType,
        documentId: this.documentId,
      } as IInvalidEmailConfirmDialogData);

      return;
    }

    this._getLatestSavedDocument()
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe(
        (document: Document) => {
          const dialogData: ISendReceiptDialogData = {
            document,
            paymentsSettings: this.paymentsSettings,
            transaction: document.payments.transactions
              .sortBy((transaction: PaymentTransaction) =>
                this._dateTimeService.getMilliseconds(transaction.datetime)
              )
              .last(),
          };
          this._dialogService.openDialog({
            type: sendReceiptDialog,
            data: dialogData,
          });
        },
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  onInvalidClientEmail({ result, data }: IConfirmDialogResult) {
    const { documentId }: IInvalidEmailConfirmDialogData = data;

    if (result && documentId) {
      this.documentDataDispatcher.goToDocumentAddClientScreen(documentId);
    }
  }

  removeDepositRequest() {
    this.onUpdateDocumentContent.emit(
      this._documentPaymentActionsBuilder.buildDocumentContentPropsFromDeposit(
        null
      )
    );
  }

  saveDepositRequest(deposit: Deposit) {
    this.onUpdateDocumentContent.emit(
      this._documentPaymentActionsBuilder.buildDocumentContentPropsFromDeposit(
        deposit
      )
    );
  }

  onEnableCardPayments() {
    const currentHref = this._navigationService.getLocationHref();
    const currentUrl = this._urlService.parse(currentHref);
    const currentPath = currentUrl.path;
    const newPath = currentPath.replace(
      `/${DOCUMENTS_NEW_ROUTE}`,
      `/${this.documentId}`
    );
    const redirectUrl = `${currentUrl.protocol}://${currentUrl.host}${newPath}?action=${CHARGE_A_CARD_ACTION}`;
    // doesn't work on local-dev
    // 'cause we don't add the port

    this.isKicEnablingPayment$.next(true);

    this.documentDataDispatcher
      .enableCardPayments(this.documentId, redirectUrl, true)
      .pipe(
        take(1),
        takeUntil(this._destroyed),
        finalize(() => this.isKicEnablingPayment$.next(false))
      )
      .subscribe(noop, () =>
        this._dialogService.closeDialog({ type: manualPaymentDialog })
      );
  }

  onDepositDisabledDialogClose({ result }: IConfirmDialogResult) {
    void this._navigationService.goTo([
      INVOICES_MASTER_ROUTE,
      this._utilsService.getConvertedDoc(this.document).id,
    ]);
  }

  onDepositDialogPreventOpen() {
    if (!this.documentAllAccess) {
      this._dialogService.openGrowthDialog(
        BannerCanvasTypesEnum.feature_lock,
        this.documentFeatureName
      );
    }
  }

  showPaymentRemindersFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this._paymentRemindersFeatureName,
      this.onGrowthCta.emit.bind(this.onGrowthCta)
    );
  }

  createManualPayment(payload: IAddManualPayment) {
    const invoiceDataDispatcher = this
      .documentDataDispatcher as IInvoiceDataDispatcher;

    invoiceDataDispatcher
      .createManualPayment(payload, true)
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe(() => this.refreshTransactions());
  }

  sendReceipt({
    documentId,
    documentBilling,
    transactionId,
    renderingOptions,
  }: ISendReceipt) {
    this._paymentsDataDispatcher.sendReceipt(
      documentId,
      documentBilling.name || documentBilling.email,
      transactionId,
      renderingOptions,
      true
    );
  }

  sendPreviewReceipt({
    document,
    transaction,
    renderingOptions,
  }: ISendReceiptPreview) {
    this._paymentsDataDispatcher.sendReceiptPreview(
      document,
      transaction,
      renderingOptions
    );
  }

  refreshTransactions() {
    // it's ok refresh the whole document to get the latest transactions 'cause it has already been saved before
    // the payment dialog is opened
    this.documentDataDispatcher.refreshEditingEntity();

    this._clientDataDispatcher.clearList(ALL_LIST_FILTER);
  }

  _getLatestSavedDocument(): Observable<Document> {
    if (!this.documentHasUnsavedChanges) {
      return of(this.document);
    }

    this._dialogService.openLoadingDialog();

    return this.documentDataDispatcher
      .saveEditingEntity()
      .pipe(finalize(() => this._dialogService.closeLoadingDialog()));
  }

  ngOnDestroy() {
    this._dialogService.closeDialog({ type: sendReceiptDialog });
    this._dialogService.closeDialog({ type: turnOnPaymentsDialog });
    this._dialogService.closeConfirmDialog(this.invalidEmailDialogId);
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this._paymentRemindersFeatureName
    );
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      this.documentFeatureName
    );
    this._dialogService.closeLoadingDialog();

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
